import React from 'react';
import { Calendar as CalendarIcon } from 'lucide-react';
import { Button } from '../../ui/button';
import { Label } from '../../ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover';
import { Calendar } from '../../ui/calendar';

export default function ScheduleStep({
  preferredDate,
  setPreferredDate,
  preferredTime,
  setPreferredTime,
  notes,
  setNotes,
  checkingZone,
  zone,
  bookingServiceType,
  allowedDates,
  availabilityMessage,
  loadingSlots,
  availableSlots,
  BOOKING_MIN_DATE,
  BOOKING_MAX_DATE,
  formatDateForDisplay
}) {
  return (
    <div className="animate-fade-in" data-testid="cleaning-step-4">
      <h2 className="text-2xl font-bold text-slate-900 font-['Outfit'] mb-2">Schedule</h2>
      <p className="text-slate-500 mb-6">When should we come?</p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <Label className="text-slate-700 font-medium">Preferred Date</Label>
          <p className="text-xs text-slate-400 mt-1 mb-2">
            {checkingZone ? "Verifying address zone..." : 
             zone ? `Zone: ${zone}` :
             bookingServiceType === 'residential' && !allowedDates ? "Enter address to see available dates" : 
             "Available: March 15-31 (no Saturdays)"}
          </p>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={`w-full h-12 mt-1 justify-start text-left font-normal ${!preferredDate ? 'text-slate-400' : 'text-slate-900'}`}
                data-testid="cleaning-date-input"
              >
                <CalendarIcon className="mr-2 h-4 w-4 text-emerald-500" />
                {preferredDate ? formatDateForDisplay(preferredDate) : 'Select a date'}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={preferredDate ? new Date(preferredDate + 'T00:00:00') : undefined}
                onSelect={(date) => {
                  if (date) {
                    const y = date.getFullYear();
                    const m = String(date.getMonth() + 1).padStart(2, '0');
                    const d = String(date.getDate()).padStart(2, '0');
                    setPreferredDate(`${y}-${m}-${d}`);
                    setPreferredTime('');
                  }
                }}
                disabled={(date) => {
                  // Disable past dates
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  if (date < today) return true;

                  // Always block Saturday
                  if (date.getDay() === 6) return true;
                  
                  // If non-residential (e.g. detailing), standard logic applies (just Sat blocked)
                  if (bookingServiceType !== 'residential') return false;
                  
                  // If residential, must have address & zone check
                  if (!allowedDates) return true; // Loading or initial state
                  if (allowedDates.length === 0) return true; // No allowed dates found (or address missing)
                  
                  const y = date.getFullYear();
                  const m = String(date.getMonth() + 1).padStart(2, '0');
                  const d = String(date.getDate()).padStart(2, '0');
                  const dateStr = `${y}-${m}-${d}`;
                  
                  return !allowedDates.includes(dateStr);
                }}
                fromDate={BOOKING_MIN_DATE}
                toDate={BOOKING_MAX_DATE}
                defaultMonth={BOOKING_MIN_DATE}
                data-testid="cleaning-calendar"
              />
            </PopoverContent>
          </Popover>
          {availabilityMessage && (
            <p className="text-sm text-red-500 mt-2" data-testid="availability-message">{availabilityMessage}</p>
          )}
        </div>
        <div>
          <Label className="text-slate-700 font-medium">Preferred Time</Label>
          {loadingSlots ? (
            <div className="mt-2 h-12 flex items-center text-sm text-slate-400">Loading available slots...</div>
          ) : availableSlots.length > 0 ? (
            <div className="mt-2 grid grid-cols-2 gap-2" data-testid="time-slots-grid">
              {availableSlots.map((slot) => (
                <button
                  key={slot.time}
                  type="button"
                  disabled={!slot.available}
                  onClick={() => setPreferredTime(slot.time)}
                  className={`px-3 py-2.5 rounded-lg text-sm font-medium border transition-all ${
                    !slot.available
                      ? 'bg-slate-100 text-slate-300 border-slate-200 cursor-not-allowed line-through'
                      : preferredTime === slot.time
                      ? 'bg-emerald-500 text-white border-emerald-500 shadow-md'
                      : 'bg-white text-slate-700 border-slate-200 hover:border-emerald-400 hover:bg-emerald-50'
                  }`}
                  data-testid={`time-slot-${slot.time.replace(/[: ]/g, '-')}`}
                >
                  {slot.time}
                  {slot.available && slot.max_crews > 1 && slot.crews_available === 1 && (
                    <span className="block text-xs text-amber-500 font-normal">1 slot left</span>
                  )}
                  {!slot.available && (
                    <span className="block text-xs text-red-400 font-normal">Full</span>
                  )}
                </button>
              ))}
            </div>
          ) : preferredDate ? (
            <div className="mt-2 h-12 flex items-center text-sm text-slate-400">
              {availabilityMessage || 'Select a valid date to see available times'}
            </div>
          ) : (
            <div className="mt-2 h-12 flex items-center text-sm text-slate-400">
              Select a date first to see available times
            </div>
          )}
        </div>
      </div>

      <div className="mt-6">
        <Label className="text-slate-700 font-medium">Notes (optional)</Label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          className="mt-2 w-full border border-slate-200 rounded-md px-3 py-2 focus:ring-2 focus:ring-emerald-500 outline-none resize-none"
          placeholder="Any special requests or notes..."
          data-testid="cleaning-notes-input"
        />
      </div>
    </div>
  );
}
